// Firebase.
import { FirebaseCtx, createInitialisedFirebase } from "igfd-commons";
import { ServerError } from "ts-packages/igfd-ui/src/components/Error-Pages/500/ServerError";

import React, { lazy, useContext } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
// Redux.
import { Provider, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";

// MUI.
import { CssBaseline, ThemeProvider } from "@mui/material";

import {
  ErrorBoundary,
  createDefaultTheme,
  useDeviceDimensions,
} from "igfd-ui/src/app-index";

// Contexts, layout, and firebase.
import { AuthStateProvider, AxiosClientContext } from "@dashboard/context";
import { WithFallback } from "@dashboard/utils/lazy";

import { MobileOverlay } from "./components/mobile-overlay/MobileOverlay";
import { config } from "./environment/config";
import { DashboardSnackbarProvider } from "./hooks/useSnackbar";
import {
  AuthenticatedLayout,
  MenusAndDishesLayout,
  RestaurantManagementLayout,
  UnauthenticatedLayout,
} from "./layouts";
import { storeWithThunkExtraArgs } from "./store/createReduxStore";
import { ReduxState } from "./types";

// pages.
const LoginPage = lazy(() => import("./components/auth/login/LoginPage"));
const SignUpPage = lazy(() => import("./components/auth/signup/SignUpPage"));
const CreateRestaurantPage = lazy(
  () => import("./components/restaurant/CreateRestaurantPage")
);
const AccountPage = lazy(async () => {
  return import("./components/account/AccountPage");
});
const ResetPasswordPage = lazy(
  () => import("./components/auth/resetPassword/ResetPasswordPage")
);
const MenuContentPage = lazy(
  () => import("./components/menus/MenuContentPage")
);
const MarketingPage = lazy(
  () => import("./components/marketing/MarketingPage")
);
const Branding = lazy(() => import("./components/branding/Branding"));
const DishesPage = lazy(() => import("./components/dishes/DishesPage"));
const ProductsPage = lazy(() => import("./components/products/ProductsPage"));
const NotFoundPage = lazy(() => import("./components/errors/NotFoundPage"));
const RestaurantPage = lazy(
  () => import("./components/restaurant/RestaurantPage")
);
const AnalyticsPage = lazy(() => import("./analytics/AnalyticsPage"));
const IntegrationsPage = lazy(
  () => import("./components/integrations/IntegrationsPage")
);

const AclPage = lazy(() => import("./components/admin/acl/AclPage"));

export const App = () => {
  const theme = createDefaultTheme({});
  const initialisedFirebase = createInitialisedFirebase(config.firebase);

  const axiosInstances = useContext(AxiosClientContext);
  const { store, persistor } = storeWithThunkExtraArgs(
    initialisedFirebase,
    axiosInstances
  );

  const queryClient = new QueryClient();
  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route element={<UnauthenticatedLayout />} ErrorBoundary={ServerError}>
          <Route index path="/login" element={WithFallback(LoginPage)} />
          <Route path="/signup" element={WithFallback(SignUpPage)} />
          <Route
            path="/reset-password"
            element={WithFallback(ResetPasswordPage)}
          />
        </Route>

        <Route element={<AuthenticatedLayout />} ErrorBoundary={ServerError}>
          <Route index path="/" element={<Navigate to="/menus" />} />
          <Route
            // This page is shown along side the header, which is depends on the number of restaurants being known.
            // Hence it sits inside the restaurant management layout.
            path="/restaurants/create"
            element={WithFallback(CreateRestaurantPage)}
          />
          <Route path="/account" element={WithFallback(AccountPage)} />
          <Route element={<RestaurantManagementLayout />}>
            <Route path="/acl" element={WithFallback(AclPage)} />
            <Route path="/settings" element={WithFallback(RestaurantPage)} />
            <Route path="/marketing" element={WithFallback(MarketingPage)} />
            <Route element={<MenusAndDishesLayout />}>
              <Route
                path="/integrations"
                element={WithFallback(IntegrationsPage)}
              />
              <Route path="/analytics" element={WithFallback(AnalyticsPage)} />
              <Route path="/products" element={WithFallback(ProductsPage)} />
              <Route
                path="/menus/:menuId?"
                element={WithFallback(MenuContentPage)}
              />
              <Route path="/dishes" element={WithFallback(DishesPage)} />
              <Route
                // branding required a valid menuId to be parsed, or the first available
                // menu. This means we need to know all available menus, hence this sits
                // inside the menus layout.
                // this is slightly inefficent, but easier to manage.
                path="/branding/:menuId?"
                element={WithFallback(Branding)}
              />
            </Route>
          </Route>
        </Route>

        <Route
          path="*"
          element={WithFallback(NotFoundPage)}
          ErrorBoundary={ServerError}
        />
      </>
    )
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <FirebaseCtx.Provider value={initialisedFirebase}>
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <DashboardSnackbarProvider>
                  <AuthStateProvider>
                    <BlockingMobileView>
                      <RouterProvider router={router} />
                    </BlockingMobileView>
                  </AuthStateProvider>
                </DashboardSnackbarProvider>
              </PersistGate>
            </Provider>
          </QueryClientProvider>
        </FirebaseCtx.Provider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

const BlockingMobileView = ({ children }: React.PropsWithChildren) => {
  const { isLargeScreen } = useDeviceDimensions();
  // allow admins to use the app on mobile.
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );

  // when an admin is accessing the site on mobile, we show the full experience.
  // However until login happens, the user is not known. and hence we don't know
  // if they are an admin. Hence this special routing exists.
  const mobileRouter = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route element={<UnauthenticatedLayout />} ErrorBoundary={ServerError}>
          <Route index path="/login-admin" element={WithFallback(LoginPage)} />
          <Route path="*" element={<MobileOverlay />} />
        </Route>
      </>
    )
  );

  if (isLargeScreen || userIsAdmin) {
    return children;
  }

  return <RouterProvider router={mobileRouter} />;
};
